import Enumerable from "linq";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { TimeMeasure } from ".";
import { IHttpRequestable } from "./IHttpRequestable";
import { IPrefetchable } from "./IPrefetchable";
import { AsyncUtility } from "./AsyncUtility";
import { LoggerFactory } from "./logger";

class Request {
  constructor(public readonly url: string, public readonly config?: AxiosRequestConfig) {
  }

  public isEqual(source: Request): boolean {
    return (source !== null)
      && (this.url === source.url)
      && ((!this.config && !source.config)
       || (this.config && source.config && (Request.paramsToString(this.config.params) === Request.paramsToString(source.config.params))));
  }

  protected static paramsToString(params: any): string {
    return params
      ? Object.keys(params).map((key: string) => key + "=" + params[key]).join("&")
      : "";
  }
}

class GetRequest extends Request {
  constructor(url: string, config?: AxiosRequestConfig) {
    super(url, config);
  }
}

class PostRequest extends Request {
  constructor(url: string, public readonly data?: any, config?: AxiosRequestConfig) {
    super(url, config);
  }

  public isEqual(source: Request): boolean {
    return super.isEqual(source)
      // 厳密にdataの完全一致を判定するのは大変.あまり複雑なPOSTをprefetchで使うとは思えないのでプロパティ1階層までの一致判定とする.
      && (source instanceof PostRequest)
      && (Request.paramsToString(this.data) === Request.paramsToString(source.data));
  }
}

interface IResult {
}

interface ISuccessed extends IResult {
  response: any;
}

function instanceOfISuccessed(instance: any): instance is ISuccessed {
  return instance
    && (instance.response !== undefined);
}

interface IFailed extends IResult {
  exception: any;
}

function instanceOfIFailed(instance: any): instance is IFailed {
  return instance
    && (instance.exception !== undefined);
}

class AcceptedRequest {
  request: Request;
  result: IResult;
}

class Fetcher {
  private _requests: AcceptedRequest[] = [];  // 本当に何でも許容するなら排他制御が必要？(prefetchで無茶しなければ大丈夫.TODO:必要なら実装.)

  async prefetch<T = any>(request: Request): Promise<AxiosResponse<T>> {
    return this.process(
      request,
      async (r: Request) => {
        const processing = this.pushNewRequest(request);
        try {
          const response = await this.processRequest(request);
          processing.result = {
            response,
          };
          return response;
        } catch (e) {
          processing.result = {
            exception: e,
          };
          throw e;
        }
      }
    );
  }

  async fetch<T = any>(request: Request): Promise<AxiosResponse<T>> {
    return this.process(
      request,
      async (r: Request) => {
        return this.processRequest(request);
      }
    );
  }

  private async process<T = any>(request: Request, process: (r: Request) => Promise<AxiosResponse<T>>): Promise<AxiosResponse<T>> {
    const found = this.find(request);
    if (found) {
      LoggerFactory.getLogger().info("prefetch結果を使用します.");
      return this.getResult(found);
    }
    return process(request);
  }

  private find(request: Request): AcceptedRequest {
    return Enumerable.from(this._requests)
      .firstOrDefault(r => r.request.isEqual(request));
  }

  private async getResult(request: AcceptedRequest): Promise<any> {
    // 以前は60秒のタイムアウト契機でrequestが空になったらエラーとなるロジックだったが、
    // 結果待ちに入っているのであればエラーとする必要はないため修正した経緯がある
    await AsyncUtility.wait(() => !!request.result, 10);
    if (instanceOfISuccessed(request.result)) {
      return request.result.response;
    } else if (instanceOfIFailed(request.result)) {
      throw request.result.exception;
    } else {
      throw new Error("prefetchが正常に完了しませんでした.");
    }
  }

  private async processRequest<T = any>(request: Request): Promise<AxiosResponse<T>> {
    return (request instanceof PostRequest)
      ? axios.post(request.url, request.data, request.config)
      : axios.get(request.url, request.config);
  }

  private pushNewRequest(request: Request): AcceptedRequest {
    const newRequest = {
      request,
      result: null as any,
    };
    this._requests.push(newRequest);
    setTimeout(() => {
      newRequest.request = null;
      this.cleanup();
    }, 60000);
    return newRequest;
  }

  private cleanup(): void {
    this._requests = Enumerable.from(this._requests)
      .where(r => !!r.request)
      .toArray();
  }
}

export class HttpClient implements IHttpRequestable, IPrefetchable {
  constructor(logger: (m: string) => void = TimeMeasure.debug) {
    this._logger = logger
      ? logger
      : TimeMeasure.off;
  }

  private readonly _logger?: (m: string) => void;

  public shouldPrefetch: boolean = false;

  private static readonly _fetcher = new Fetcher();

  private get fetch(): <T = any>(request: Request) => Promise<AxiosResponse<T>> {
    return this.shouldPrefetch
      ? r => HttpClient._fetcher.prefetch(r)
      : r => HttpClient._fetcher.fetch(r);
  }

  private get logPrefix(): string {
    return this.shouldPrefetch
      ? "prefetch)"
      : "";
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const _config = HttpClient.toDefaultBaseURLConfig(config);
    return TimeMeasure.logAsync(
      this.logPrefix + "GET: " + url + " ",
      async () => {
        return this.fetch(new GetRequest(url, _config));
      },
      (_config.params) ? HttpClient.paramsToString(_config.params) : null,
      this._logger
    );
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    const _config = HttpClient.toDefaultBaseURLConfig(config);
    return TimeMeasure.logAsync(
      this.logPrefix + "POST: " + url + " ",
      async () => {
        return this.fetch(new PostRequest(url, data, _config));
      },
      (_config.params) ? HttpClient.paramsToString(_config.params) : null,
      this._logger
    );
  }

  private static toDefaultBaseURLConfig(config: AxiosRequestConfig = {}): AxiosRequestConfig {
    if (!config.baseURL) {
      config.baseURL = process.env.ASSET_PATH;
    }
    return config;
  }

  private static paramsToString(params: any): string {
    return Object.keys(params).map((key: string) => key + "=" + params[key]).join("&");
  }
}
